/**
 * The Changes report's grouping (spec 028): a turn's plain changes as one
 * card per scene, in story order, with lines merged across characters
 * ("Dana, Marco, Jules made bigger"). Pure.
 */
import type { Orientation } from '../../../../Shared/src/@types';
import type { PlainChange } from '../../../../Shared/src/operations';

/** A plain change with the summary of the step it came from (the line's tooltip, D-4). */
export type SummarizedChange = PlainChange & { summary: string };

export interface ChangeLine {
  who: string[];
  text: string;
  /** Present only when the turn's lines do not all share one orientation. */
  orientation?: Orientation;
  warn?: true;
  /** The summaries of the steps this line came from, in order, once each. */
  summaries: string[];
}

export interface ChangeCard {
  /** The scene key; absent for the Story map card. */
  scene?: string;
  sceneName: string;
  /** The scene's route stop (1-based); absent off the route and for the Story map card. */
  stop?: number;
  shared: boolean;
  /** The story-wide changes' card. */
  storyMap: boolean;
  lines: ChangeLine[];
}

export interface ChangesGroup {
  cards: ChangeCard[];
  /** Set when every line of the turn has this orientation: the report's header shows it, the lines none. */
  orientation?: Orientation;
}

export const ORIENTATION_TAG: Readonly<Record<Orientation, string>> = {
  desktop: 'Desktop only',
  portrait: 'Portrait only',
  landscape: 'Landscape only',
};

/** Every plain change of a turn's steps, each with its step's summary; undefined when a step has no `change` (a stale Service). */
export function changesOfSteps(steps: readonly { summary: string; change?: PlainChange[] }[]): SummarizedChange[] | undefined {
  if (steps.some((s) => !Array.isArray(s.change))) return undefined;
  return steps.flatMap((s) => s.change!.map((c) => ({ ...c, summary: s.summary })));
}

const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
const capitalise = (t: string) => (t ? t[0].toUpperCase() + t.slice(1) : t);

/**
 * A line as read: `<who joined with ", "> <text>`, or `text` capitalised when
 * nobody. A possessive text ("'s talking now plays faster", spec 035) joins
 * without the space.
 */
export function lineText(line: Pick<ChangeLine, 'who' | 'text'>): string {
  if (!line.who.length) return capitalise(line.text);
  return `${line.who.join(', ')}${line.text.startsWith("'s ") ? '' : ' '}${line.text}`;
}

/** D-3: the name without a leading route stop it repeats ("3 Blame" at stop 3 is "Blame"). */
export function cardName(name: string, stop: number | undefined): string {
  if (stop === undefined) return name;
  const prefix = `${stop} `;
  return name.startsWith(prefix) && name.length > prefix.length ? name.slice(prefix.length) : name;
}

interface Working extends SummarizedChange { summaries: string[] }

/**
 * A card's changes netted over the turn: a sprite added this turn shows only
 * `added` (renamed: under its new name; removed again: nothing), and a value
 * a later step put back shows nothing.
 */
function netCard(changes: readonly SummarizedChange[]): Working[] {
  let list: (Working | null)[] = changes.map((c) => ({ ...c, summaries: [c.summary] }));

  // A sprite added this turn.
  const added = new Map<string, Working>();
  list = list.map((c) => {
    if (!c || !c.target) return c;
    const add = added.get(c.target);
    if (c.text === 'added') { added.set(c.target, c); return c; }
    if (!add) return c;
    add.summaries.push(...c.summaries);
    if (c.text === 'removed') { add.text = ''; return null; }
    const renamed = /^renamed to (.+)$/.exec(c.text);
    if (renamed) add.who = [renamed[1]];
    return null;
  });
  list = list.map((c) => (c && c.text === '' ? null : c));

  // The same value changed more than once.
  const chains = new Map<string, Working[]>();
  for (const c of list) if (c?.net) chains.set(c.net.key, [...(chains.get(c.net.key) ?? []), c]);
  for (const chain of chains.values()) {
    const first = chain[0];
    const last = chain[chain.length - 1];
    const from = first.net!.from;
    const to = last.net!.to;
    const keep = same(from, to) ? null : last;
    if (keep) {
      keep.summaries = chain.flatMap((c) => c.summaries);
      keep.net = { key: last.net!.key, from, to };
      if ((keep.text === 'made bigger' || keep.text === 'made smaller') && typeof from === 'number' && typeof to === 'number') {
        keep.text = to > from ? 'made bigger' : 'made smaller';
      }
    }
    list = list.map((c) => (c && chain.includes(c) && c !== keep ? null : c));
  }
  return list.filter((c): c is Working => c !== null);
}

/** Lines with the same text, orientation and warning merged; `who` the union in first-seen order. */
function mergeLines(changes: readonly Working[]): ChangeLine[] {
  const lines = new Map<string, ChangeLine>();
  for (const c of changes) {
    const key = `${c.text}|${c.orientation ?? ''}|${c.warn ? 1 : 0}`;
    let line = lines.get(key);
    if (!line) {
      line = { who: [], text: c.text, ...(c.orientation ? { orientation: c.orientation } : {}), ...(c.warn ? { warn: true as const } : {}), summaries: [] };
      lines.set(key, line);
    }
    for (const w of c.who) if (!line.who.includes(w)) line.who.push(w);
    for (const s of c.summaries) if (!line.summaries.includes(s)) line.summaries.push(s);
  }
  return [...lines.values()];
}

/**
 * A turn's plain changes as cards: one per scene in route order (`order`:
 * the scene keys of the route's stops, first stop first), then scenes off
 * the route in first-seen order, then a "Story map" card for story-wide
 * changes. A shared orientation moves from the lines to the report.
 */
export function groupChanges(changes: readonly SummarizedChange[], order: readonly string[] = []): ChangesGroup {
  const buckets = new Map<string, SummarizedChange[]>();
  for (const c of changes) {
    const key = c.scene ?? '';
    buckets.set(key, [...(buckets.get(key) ?? []), c]);
  }
  const stopOf = new Map(order.map((key, i) => [key, i + 1]));
  const cards: ChangeCard[] = [];
  for (const [key, list] of buckets) {
    const lines = mergeLines(netCard(list));
    if (!lines.length) continue;
    if (key === '') {
      cards.push({ sceneName: 'Story map', shared: false, storyMap: true, lines });
      continue;
    }
    const named = [...list].reverse().find((c) => c.sceneName);
    const stop = stopOf.get(key);
    cards.push({
      scene: key,
      sceneName: named?.sceneName ?? '',
      ...(stop !== undefined ? { stop } : {}),
      shared: list.some((c) => c.shared),
      storyMap: false,
      lines,
    });
  }
  const rank = (c: ChangeCard) => (c.storyMap ? 2 : c.stop !== undefined ? 0 : 1);
  const firstSeen = new Map(cards.map((c, i) => [c, i]));
  cards.sort((a, b) => rank(a) - rank(b)
    || (rank(a) === 0 ? a.stop! - b.stop! : firstSeen.get(a)! - firstSeen.get(b)!));

  const all = cards.flatMap((c) => c.lines);
  const first = all[0]?.orientation;
  const shared = first && all.every((l) => l.orientation === first) ? first : undefined;
  if (shared) for (const l of all) delete l.orientation;
  return { cards, ...(shared ? { orientation: shared } : {}) };
}

/** The number of scene cards (the Story map card is not a scene). */
export const sceneCardCount = (group: ChangesGroup): number => group.cards.filter((c) => !c.storyMap).length;

/** The story report's header (`CHANGED IN 3 SCENES`, `CHANGED IN 1 SCENE`, or `CHANGED` for the Story map alone). */
export function reportTitle(group: ChangesGroup): string {
  const n = sceneCardCount(group);
  return n === 0 ? 'CHANGED' : `CHANGED IN ${n} SCENE${n === 1 ? '' : 'S'}`;
}

// ─── Avatars (D-6) ──────────────────────────────────────────────────────────

/** Six light fills; the initial is drawn dark on them. */
export const AVATAR_COLOURS = ['#f9c9a8', '#b9e3c6', '#bcd4f6', '#f4c2d7', '#e8dcab', '#d3c4f2'] as const;

/** The colour of a name (FNV-1a of the trimmed, lower-cased name): stable across scenes, turns and reloads. */
export function avatarColour(name: string): string {
  let h = 2166136261;
  for (const ch of name.trim().toLowerCase()) {
    h ^= ch.codePointAt(0)!;
    h = Math.imul(h, 16777619) >>> 0;
  }
  return AVATAR_COLOURS[h % AVATAR_COLOURS.length];
}

/** Up to two initials: "Dana" → "D", "Mr Smith" → "MS". */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return words.slice(0, 2).map((w) => [...w][0].toUpperCase()).join('') || '?';
}
